import { FC, useEffect, useState } from 'react';
import { Image, Text, Button, Box } from 'theme-ui';
import Modal from 'shared/Modal';
import { TimeSlider } from 'shared/TimeSlider';
import { TrimModalProps } from './types';
import { getImageAspectRatio } from 'utils/getAspectRatio';
import useAsync from 'react-use/lib/useAsync';
import { MAX_PREVIEW_HEIGHT } from 'constants/trim';
import analytics from 'utils/analytics';
import { useTranslator } from '@picsart/rc/services/localization';
import { MIN_SCENE_DURATION } from 'constants/scenes';

const ImageDurationModal: FC<TrimModalProps> = ({
  src,
  opened,
  duration: initialDuration,
  maxDuration,
  closeHandler,
  applyHandler: applyFn
}) => {
  const [duration, setDuration] = useState(initialDuration);
  const translator = useTranslator();
  useEffect(() => setDuration(initialDuration), [opened]);

  const applyHandler = () => {
    analytics.trimAction({ media_type: 'photo' });
    applyFn(duration, 0);
    closeHandler();
  };

  const { value: aspectRatio } = useAsync(() => getImageAspectRatio(src), [src]);

  const imageSize =
    aspectRatio < 1
      ? { width: 'auto', maxHeight: MAX_PREVIEW_HEIGHT }
      : { width: '100%', height: 'auto' };

  return (
    <Modal
      color="transparent"
      opened={opened}
      closeHandler={closeHandler}
      title={translator('Trim_Tool')}
    >
      <Box sx={{ width: 272, mx: 3, mb: 6 }} data-test="trim-modal">
        <Image variant="bordered" src={src} sx={{ ...imageSize, mx: 0, mb: 6 }} />
        <Text variant="body" sx={{ fontWeight: 'heading', textAlign: 'left', mb: 1 }}>
          {translator('Duration')}
        </Text>
        <TimeSlider
          min={0}
          minDiff={MIN_SCENE_DURATION}
          max={maxDuration}
          value={duration}
          onChange={setDuration}
          pointsCount={14}
          sx={{ mb: 7 }}
        />
        <Button variant="primary" onClick={applyHandler} data-test="apply">
          {translator('Apply')}
        </Button>
      </Box>
    </Modal>
  );
};

ImageDurationModal.displayName = 'ImageDurationModal';

export default ImageDurationModal;
